import { BuilderComponent, BuilderContent } from '@builder.io/react';
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';

import ArticleAuthorInfo from '@/components/article-author-info/article-author-info';
import ArticleHero from '@/components/article-hero/article-hero';
import Loading from '@/components/loading';
import MetaBlock from '@/components/meta-block/MetaBlock';
import NotFound from '@/components/not-found/not-found';
import RoomstayBlogHighlightArticle from '@/components/roomstay-blog-highlight-article/roomstay-blog-highlight-article';
import RoomstayBlogRelatedArticles from '@/components/roomstay-blog-related-articles/roomstay-blog-related-articles';
import ShareIcons from '@/components/share-icons/share-icons';
import { ThemeStyles, useThemeContext } from '@/contexts/ThemeContext';
import ArticleModel from '@/models/ArticleModel';
import { ArticleTheme } from '@/types/article.d';
import { BUILDER_MODEL } from '@/types/builder.d';
import { getRelatedArticle } from '@/utils/Article';
import { getBlockDataMap } from '@/utils/Builder';

import styles from './article.module.scss';

export async function getStaticProps({ params }: GetStaticPropsContext<any>) {
    const slug = params.slug;
    const pageSlug = Array.isArray(slug) ? slug.join('/') : slug;
    const article = await ArticleModel.findArticle({
        params: { url: pageSlug },
        includeUnpublished: true,
    });

    if (!article) {
        return <NotFound />;
    }

    const convertedArticle = ArticleModel.fromBuilderContent(article);
    const relatedArticles = await getRelatedArticle(convertedArticle);
    const upnextArticle = ArticleModel.fromBuilderContent(await ArticleModel.findNextById(article.id, article.createdDate));

    return {
        props: { page: article, relatedArticles, upnextArticle },
        revalidate: 5,
    };
}

export async function getStaticPaths() {
    const pages = await ArticleModel.getAllArticles(true, false, 'data.url');
    const paths = pages.filter((page: any) => page.data.url).map((page: any) => ArticleModel.convertBuilderURL2Slug(page.data.url));
    return {
        paths,
        fallback: true,
    };
}

export const getArticleById = async (articleId?: string | string[], enrich?: boolean) => {
    if (!articleId) return;
    const article = await ArticleModel.findArticle({
        params: { modelId: Array.isArray(articleId) ? articleId[0] : articleId },
        enrich,
    });
    return article;
};

export default function ArticlePage(
    props: InferGetStaticPropsType<typeof getStaticProps> & {
        builderContent: BuilderContent;
    }
) {
    const { page, upNextArticle, relatedArticles } = props;
    const article = useMemo(() => ArticleModel.fromBuilderContent(page), [page]);
    const router = useRouter();

    const theme = useThemeContext();

    useEffect(() => {
        if (theme.themeStyle === ThemeStyles.Blog) return;
        theme.setThemeStyle(ThemeStyles.Blog);
        return () => {
            theme.setThemeStyle(null);
        };
    }, []);

    useEffect(() => {
        theme.setArticleTheme(article.theme || ArticleTheme.Light);

        return () => {
            theme.setArticleTheme(null);
        };
    }, [article]);

    let targetUrl = article?.url;
    if (typeof window !== 'undefined') {
        targetUrl = `${window?.origin || ''}${article?.url}`;
    }

    if (router.isFallback || !article) {
        return <Loading />;
    }

    const schema = {
        '@context': 'https://schema.org/',
        '@type': 'Article',
        headline: article.title,
        image: article.leadImage || article.leadPortraitImage || '',
        author: [
            {
                '@type': 'Person',
                name: article.author?.name,
                jobTitle: article.author?.jobTitle,
            },
            {
                '@type': 'Organization',
                name: 'Roomstay',
                url: 'https://roomstay.io/',
            },
        ],
        datePublished: article.firstPublished ? new Date(article.firstPublished).toJSON() : null,
        dateModified: article.lastUpdated ? new Date(article.lastUpdated).toJSON() : null,
        publisher: {
            '@type': 'Organization',
            name: 'Roomstay',
            logo: {
                '@type': 'ImageObject',
                url: 'https://roomstay.io/favicon-32x32.png',
            },
        },
    };

    const generateSchemas = article.schemas?.map(({ type, value }, index) => {
        return <script key={index} type={type} dangerouslySetInnerHTML={{ __html: value }} />;
    });

    let canonicalSlug = article?.url;
    if (canonicalSlug?.endsWith('/')) {
        canonicalSlug = canonicalSlug.replace(/\/$/, '');
    }
    const canonicalUrl = process.env.SITE_URL || 'https://www.roomstay.io' + canonicalSlug;

    return (
        <>
            <MetaBlock title={article.seoTitle ?? article.title} description={article.seoDescription} leadImage={article.leadImage} keywords={article.keywords}>
                <link rel="canonical" href={canonicalUrl}/>
                <script type="application/ld+json">{JSON.stringify(schema, null, 2)}</script>
                {generateSchemas}
            </MetaBlock>
            <ArticleHero article={article} />

            <BuilderComponent model={BUILDER_MODEL.ARTICLE} content={page} data={getBlockDataMap(page)} />
            <div className={styles.footer}>
                <div className={styles.divider} />
                <ArticleAuthorInfo articleAuthor={article.author} />
                <ShareIcons shareUrl={targetUrl} />
                <RoomstayBlogHighlightArticle fixedArticle={upNextArticle} arrowText="Up Next" />
                <RoomstayBlogRelatedArticles articles={relatedArticles} />
            </div>
        </>
    );
}
